(() => {
  const initialized = new WeakSet();
  const mobileQuery = window.matchMedia('(max-width: 749px)');

  function isRendered(element) {
    if (!element || element.hidden) return false;
    const style = window.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden';
  }

  function getGalleryParts(gallery) {
    const component = gallery.querySelector('[id^="GalleryViewer-"]');
    const slider = component?.querySelector('[id^="Slider-Gallery-"]');
    if (!component || !slider) return null;

    const items = [...slider.querySelectorAll(':scope > .product__media-item')].filter(isRendered);
    return { component, slider, items };
  }

  function updateCounter(gallery) {
    if (!mobileQuery.matches) return;
    const parts = getGalleryParts(gallery);
    if (!parts || !parts.items.length) return;

    const { component, slider, items } = parts;
    const counterCurrent = component.querySelector('.slider-counter--current');
    const counterTotal = component.querySelector('.slider-counter--total');
    const previousButton = component.querySelector('button[name="previous"]');
    const nextButton = component.querySelector('button[name="next"]');

    const firstOffset = items[0].offsetLeft;
    const secondOffset = items[1]?.offsetLeft;
    const itemOffset = secondOffset != null ? secondOffset - firstOffset : items[0].clientWidth;
    const maxScroll = Math.max(0, slider.scrollWidth - slider.clientWidth);
    const reachableCount = itemOffset > 1
      ? Math.min(items.length, Math.max(1, Math.round(maxScroll / itemOffset) + 1))
      : items.length;

    let nearestIndex = 0;
    let nearestDistance = Number.POSITIVE_INFINITY;
    items.slice(0, reachableCount).forEach((item, index) => {
      const distance = Math.abs(item.offsetLeft - slider.scrollLeft);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    });

    const current = Math.min(reachableCount, nearestIndex + 1);
    if (counterCurrent) counterCurrent.textContent = String(current);
    if (counterTotal) counterTotal.textContent = String(reachableCount);

    // Keep Dawn's slider state in sync so the arrows never point to a phantom page.
    if ('sliderItems' in component) component.sliderItems = component.querySelectorAll('[id^="Slide-"]');
    if ('sliderItemsToShow' in component) component.sliderItemsToShow = items.slice(0, reachableCount);
    if ('sliderItemOffset' in component && itemOffset > 1) component.sliderItemOffset = itemOffset;
    if ('slidesPerPage' in component) component.slidesPerPage = 1;
    if ('totalPages' in component) component.totalPages = reachableCount;
    if ('currentPage' in component) component.currentPage = current;

    if (previousButton) previousButton.toggleAttribute('disabled', current <= 1);
    if (nextButton) nextButton.toggleAttribute('disabled', current >= reachableCount);
  }

  function queueUpdate(gallery) {
    window.requestAnimationFrame(() => {
      updateCounter(gallery);
      window.setTimeout(() => updateCounter(gallery), 80);
      window.setTimeout(() => updateCounter(gallery), 360);
    });
  }

  function initializeGallery(gallery) {
    if (!gallery || initialized.has(gallery)) return;
    initialized.add(gallery);

    const parts = getGalleryParts(gallery);
    if (!parts) return;

    parts.slider.addEventListener('scroll', () => queueUpdate(gallery), { passive: true });
    parts.slider.querySelectorAll('img, video').forEach((media) => {
      media.addEventListener('load', () => queueUpdate(gallery), { once: true });
      media.addEventListener('loadedmetadata', () => queueUpdate(gallery), { once: true });
    });

    const resizeObserver = new ResizeObserver(() => queueUpdate(gallery));
    resizeObserver.observe(parts.slider);
    gallery._lootGalleryResizeObserver = resizeObserver;

    queueUpdate(gallery);
  }

  function initializeAll(scope = document) {
    scope.querySelectorAll?.('product-info.loot-product-page--sharp media-gallery').forEach(initializeGallery);
  }

  document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true });
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  document.addEventListener('product-info:loaded', (event) => initializeAll(event.target));

  mobileQuery.addEventListener?.('change', () => {
    document.querySelectorAll('product-info.loot-product-page--sharp media-gallery').forEach(queueUpdate);
  });

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches?.('media-gallery')) initializeGallery(node);
        initializeAll(node);
      }
    }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
